import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Activity } from "lucide-react";
import { api } from "../../lib/api";

const BASE = `${process.env.REACT_APP_MEME_API_URL || process.env.REACT_APP_BACKEND_URL}/api/meme`;
const ENDPOINTS = ["status", "radar?limit=1", "positions", "trades?limit=1", "alerts?limit=1", "lab", "dataset/stats", "research/counterfactuals", "research/entry-windows", "research/factors", "wallet"];

const Dot = ({ ok, degraded }) => <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ok ? (degraded ? "var(--r2-amber)" : "var(--r2-emerald)") : "var(--r2-red)", boxShadow: ok && !degraded ? "0 0 6px var(--r2-emerald)" : undefined }} />;

export const HealthCard = () => {
  const [eps, setEps] = useState([]);
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const probe = useCallback(async () => {
    const results = await Promise.all(ENDPOINTS.map(async (ep) => {
      const t0 = performance.now();
      try { const r = await axios.get(`${BASE}/${ep}`); return { ep: ep.split("?")[0], ok: true, code: r.status, ms: Math.round(performance.now() - t0) }; }
      catch (e) { return { ep: ep.split("?")[0], ok: false, code: e?.response?.status || "NET", ms: Math.round(performance.now() - t0), detail: e?.response?.data?.detail }; }
    }));
    setEps(results);
    try { setHealth(await api.meme.health()); } catch (e) { setHealth({ ok: false, checks: {}, error: e?.response?.status || "unreachable" }); }
  }, []);
  useEffect(() => { probe(); const id = setInterval(probe, 20000); return () => clearInterval(id); }, [probe]);
  const bad = eps.filter((e) => !e.ok).length + Object.values(health?.checks || {}).filter((c) => !c.ok).length;
  const degraded = Object.values(health?.checks || {}).some((c) => c.degraded);
  return (
    <div className="relative" data-testid="health-card">
      <button onClick={() => setOpen((v) => !v)} data-testid="health-toggle" className="flex items-center gap-1.5 text-[9px] font-mono tracking-[0.18em] text-[var(--r2-text2)] hover:text-[var(--r2-text1)]">
        <Activity size={11} style={{ color: bad ? "var(--r2-red)" : degraded ? "var(--r2-amber)" : "var(--r2-emerald)" }} />
        <span data-testid="health-summary">{eps.length ? (bad ? `${bad} ISSUE${bad > 1 ? "S" : ""}` : degraded ? "DEGRADED" : "ALL SYSTEMS OK") : "CHECKING…"}</span>
        {health?.standalone && <span className="text-[7px] border border-[var(--r2-border)] rounded px-1">STANDALONE</span>}
      </button>
      {open && createPortal(
        <div className="fixed right-3 top-12 z-[70] w-[440px] r2-glass border border-[var(--r2-border)] rounded-md p-3 space-y-3 shadow-2xl bg-[#070b12]/95" data-testid="health-panel">
          <div>
            <div className="text-[7.5px] font-mono tracking-[0.2em] text-[var(--r2-text2)] mb-1">API ENDPOINTS · {BASE.replace(/^https?:\/\//, "")}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">{eps.map((e) => (
              <div key={e.ep} className="flex items-center gap-1.5 text-[8.5px] font-mono" data-testid={`health-ep-${e.ep.replace(/\//g, "-")}`} data-ok={e.ok}>
                <Dot ok={e.ok} /><span className="flex-1 truncate text-[var(--r2-text1)]">/{e.ep}</span><span className="tabular-nums text-[var(--r2-text2)]">{e.code} · {e.ms}ms</span>
              </div>))}</div>
          </div>
          <div>
            <div className="text-[7.5px] font-mono tracking-[0.2em] text-[var(--r2-text2)] mb-1">ENGINE COMPONENTS</div>
            {health?.checks ? Object.entries(health.checks).map(([k, c]) => (
              <div key={k} className="flex items-start gap-1.5 text-[8.5px] font-mono py-0.5" data-testid={`health-check-${k}`} data-ok={c.ok}>
                <span className="pt-1"><Dot ok={c.ok} degraded={c.degraded} /></span><span className="w-[92px] uppercase tracking-[0.1em] text-[var(--r2-text1)] shrink-0">{k.replace(/_/g, " ")}</span><span className="text-[var(--r2-text2)] break-words">{c.detail || (c.ok ? "ok" : "failed")}{c.last_error ? ` · ${c.last_error}` : ""}</span>
              </div>)) : <div className="text-[8.5px] font-mono text-[var(--r2-red)]">/health unreachable ({health?.error ?? "…"})</div>}
          </div>
          <div className="text-[7.5px] font-mono text-[var(--r2-text2)]">Probes every 20s · red = failing call (status code shown) · amber = degraded fallback</div>
        </div>, document.body)}
    </div>
  );
};
